import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import useShopifyEmbedStatus from "@babackapp/private-api-client/client/use-shopify-embed-status";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { Button } from "@babackapp/ui/components/button";
import useTranslation from "@babackapp/translations/use-translation";
import { ExternalLink, Puzzle } from "lucide-react";

const STEP_KEYS = [
  "settings.customerPortal.customization.embedActivation.oneClick.step1",
  "settings.customerPortal.customization.embedActivation.oneClick.step2",
] as const;

export default function EmbedActivationCard() {
  const { data: shop } = useCurrentShopSuspense();
  const { data: embedStatus } = useShopifyEmbedStatus(
    shop.shopifyUrl ? shop.id : null,
  );
  const t = useTranslation();

  // App embeds only exist on Shopify storefronts.
  if (!shop.shopifyUrl) return null;

  const activationUrl = embedStatus?.activationUrl;

  return (
    <Card className="w-full">
      <CardContent className="flex flex-col gap-4 mt-6">
        <div className="flex items-start gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10">
            <Puzzle className="size-4 text-primary" />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium leading-none">
              {t(
                "settings.customerPortal.customization.embedActivation.title",
              )}
            </p>
            <p className="text-sm text-muted-foreground">
              {t(
                "settings.customerPortal.customization.embedActivation.intro",
              )}
            </p>
          </div>
        </div>

        <ol className="ml-12 flex list-none flex-col gap-2">
          {STEP_KEYS.map((key, index) => (
            <li key={key} className="flex items-start gap-3 text-sm">
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
                {index + 1}
              </span>
              <span className="text-muted-foreground">{t(key)}</span>
            </li>
          ))}
        </ol>

        <div className="ml-12 flex flex-col gap-1.5">
          <Button
            asChild
            variant="outline"
            className="w-fit aria-disabled:pointer-events-none aria-disabled:opacity-50"
          >
            <a
              href={activationUrl ?? undefined}
              aria-disabled={!activationUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(
                "settings.customerPortal.customization.embedActivation.button",
              )}
              <ExternalLink className="size-4" />
            </a>
          </Button>
          <p className="text-xs text-muted-foreground">
            {t(
              "settings.customerPortal.customization.embedActivation.oneClick.buttonHint",
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
